import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Landmark, Loader2, Send, RotateCcw, FileText } from 'lucide-react';
import { getCapitalDetails, saveShareCapitalData } from '../../api/AccountsAPI/shareCapitalAPI';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';

// Legacy /Accounts/ShareCapitalUpdation (Views/Accounts/ShareCapitalUpdation.cshtml).
// Authorised / Issued / Paid-up capital: shares × price = amount; date, remarks and a PDF are required.
// spSaveShareCapitalData returns 'Submited$<file>'; the PDF is uploaded to CapitalShare under that name.

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';
const readonlyCls = inputCls + ' bg-gray-50 dark:bg-gray-900/40';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const ROWS = [
    { key: 'AC', label: 'Authorised Capital', shares: 'AccountCapitalNoofShare', price: 'AccountCapitalEachSharePrice', amount: 'AccountCapitalAmount' },
    { key: 'IC', label: 'Issued Capital',     shares: 'IssuedCapitalNoofShare',  price: 'IssuedCapitalEachSharePrice',  amount: 'IssuedCapitalAmount' },
    { key: 'PUC', label: 'Paid Up Capital',   shares: 'PaidCapitalNoofShare',    price: 'PaidCapitalEachSharePrice',    amount: 'PaidCapitalAmount' },
];
const emptyForm = { AC: { shares: '', price: '' }, IC: { shares: '', price: '' }, PUC: { shares: '', price: '' } };

const amountOf = (r) => (r.shares !== '' && r.price !== '' ? (parseFloat(r.shares) * parseFloat(r.price)) : null);
const fmt = (n) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const ShareCapitalUpdation = () => {
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const createdBy = userData?.userName || '';

    const [form, setForm] = useState(emptyForm);
    const [date, setDate] = useState('');
    const [remarks, setRemarks] = useState('');
    const [file, setFile] = useState(null);
    const [fileKey, setFileKey] = useState(0);
    const [saving, setSaving] = useState(false);
    const [capital, setCapital] = useState([]);
    const [capitalLoading, setCapitalLoading] = useState(false);

    const loadCapital = async () => {
        setCapitalLoading(true);
        try {
            const res = await getCapitalDetails();
            const d = res?.data?.Data ?? res?.data ?? [];
            setCapital(Array.isArray(d) ? d : []);
        } catch { setCapital([]); }
        finally { setCapitalLoading(false); }
    };
    useEffect(() => { loadCapital(); }, []);

    const setCell = (row, field, value) => {
        // legacy IsNumeric: whole numbers for shares; price allows decimals
        if (field === 'shares' && value !== '' && !/^\d+$/.test(value)) return;
        if (field === 'price' && value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
        setForm((p) => ({ ...p, [row]: { ...p[row], [field]: value } }));
    };

    const pickPdf = (e) => {
        const f = e.target.files?.[0] || null;
        if (f && f.name.split('.').pop().toLowerCase() !== 'pdf') {
            toast.error('Invalid Upload File. Upload only PDF File');
            e.target.value = '';
            setFile(null);
            return;
        }
        setFile(f);
    };

    const reset = () => {
        setForm(emptyForm); setDate(''); setRemarks(''); setFile(null); setFileKey((k) => k + 1);
    };

    const handleSubmit = async () => {
        const names = { AC: 'Authorised Capital', IC: 'Issued Capital', PUC: 'PaidUp Capital' };
        for (const r of ROWS) {
            const v = form[r.key];
            if (!v.shares || Number(v.shares) === 0) { toast.warn(`Enter Number of Share For ${names[r.key]}`); return; }
            if (!v.price || Number(v.price) === 0) { toast.warn(`Enter Price of Each Share For ${names[r.key]}`); return; }
        }
        if (Number(form.PUC.shares) > Number(form.IC.shares)) { toast.warn('Paid Up Capital Share is Not More Than Issue Capital Share'); return; }
        if (Number(form.IC.shares) > Number(form.AC.shares)) { toast.warn('Issued Capital Share is Not More Than Authorised Capital Share'); return; }
        if (!file) { toast.warn('Upload Pdf File'); return; }
        if (!date) { toast.warn('Select Date'); return; }
        if (!remarks.trim()) { toast.warn('Select Remarks'); return; }

        const payload = {};
        for (const r of ROWS) {
            payload[r.shares] = parseInt(form[r.key].shares, 10);
            payload[r.price] = parseFloat(form[r.key].price);
            payload[r.amount] = Number(amountOf(form[r.key]).toFixed(2));
        }
        Object.assign(payload, {
            Date: date,
            Remarks: remarks.trim(),
            Filechk: 'Yes',
            Extension: file.name.split('.').pop(),
            RoleId: parseInt(roleId, 10) || 0,
            CreatedBy: createdBy,
        });

        setSaving(true);
        try {
            const res = await saveShareCapitalData(payload);
            const status = String(res?.data?.Data ?? res?.data ?? '').trim();
            const [result, fileName] = status.split('$');
            if (result !== 'Submited') { toast.error(status || 'Failed to submit share capital'); return; }
            if (fileName) {
                try { await uploadFileToS3(file, S3_FOLDERS.CAPITAL_SHARE, fileName); }
                catch (e) { toast.warning(`Saved, but the PDF upload failed: ${e?.Message || e?.message || e}`); }
            }
            toast.success('Share Capital Data Submitted Successfully');
            reset();
            loadCapital();
        } catch (e) {
            toast.error(e?.response?.data?.Message || e?.message || 'Failed to submit share capital');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">
            <div className="max-w-5xl mx-auto space-y-5">
                <div className="rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center border border-white/20"><Landmark className="h-6 w-6" /></div>
                    <div>
                        <h1 className="text-xl font-black">Share Capital Updation</h1>
                        <p className="text-sm text-white/70">Authorised, issued and paid-up capital</p>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-3">Capital Details</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    {['Type', 'Date', 'No of Shares', 'Price of Each Share', 'Amount'].map((h) => (
                                        <th key={h} className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {capitalLoading ? (
                                    <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
                                ) : capital.length === 0 ? (
                                    <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400 text-xs">No capital details</td></tr>
                                ) : capital.map((c, i) => (
                                    <tr key={i} className="border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200">
                                        <td className="px-3 py-2">{c.CapitalType}</td>
                                        <td className="px-3 py-2">{c.ShareCapitalDate}</td>
                                        <td className="px-3 py-2">{c.shares}</td>
                                        <td className="px-3 py-2">{c.Eachshare}</td>
                                        <td className="px-3 py-2 text-right">{c.shareAmount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
                    <div className="grid grid-cols-4 gap-3 text-xs font-bold text-gray-500 uppercase">
                        <div /> <div>No of Shares</div> <div>Price Of Each Share</div> <div>Amount</div>
                    </div>
                    {ROWS.map((r) => (
                        <div key={r.key} className="grid grid-cols-4 gap-3 items-center">
                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">{r.label}</div>
                            <input className={inputCls} inputMode="numeric" value={form[r.key].shares}
                                onChange={(e) => setCell(r.key, 'shares', e.target.value)} disabled={saving} />
                            <input className={inputCls} inputMode="decimal" value={form[r.key].price}
                                onChange={(e) => setCell(r.key, 'price', e.target.value)} disabled={saving} />
                            <input className={readonlyCls} readOnly value={fmt(amountOf(form[r.key]))} />
                        </div>
                    ))}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label required>Upload Document (PDF)</Label>
                            <input key={fileKey} type="file" accept="application/pdf,.pdf" className={inputCls} onChange={pickPdf} disabled={saving} />
                        </div>
                        <div>
                            <Label required>Date</Label>
                            <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} disabled={saving} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={1} className={inputCls} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={saving} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={reset} disabled={saving}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                        <button type="button" onClick={handleSubmit} disabled={saving}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
                        </button>
                    </div>
                    <p className="text-[11px] text-gray-400 flex items-center gap-1"><FileText className="w-3 h-3" /> Goes for verification after submit.</p>
                </div>
            </div>
        </div>
    );
};

export default ShareCapitalUpdation;
